import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronRight } from 'lucide-react';
import SEO from '../seo/SEO';

const LAST_UPDATED = 'February 2026';

const SECTIONS = [
  { id: 'agreement', title: '1. Agreement to Terms' },
  { id: 'definitions', title: '2. Definitions' },
  { id: 'services', title: '3. Services Provided' },
  { id: 'account', title: '4. Account & Onboarding' },
  { id: 'pricing', title: '5. Pricing & Payment' },
  { id: 'inventory', title: '6. Inventory, Receiving & Storage' },
  { id: 'fulfillment', title: '7. Order Fulfillment' },
  { id: 'shipping', title: '8. Shipping & Carriers' },
  { id: 'restricted', title: '9. Restricted & Prohibited Items' },
  { id: 'returns', title: '10. Returns Processing' },
  { id: 'liability', title: '11. Risk of Loss, Insurance & Liability' },
  { id: 'client-obligations', title: '12. Client Obligations' },
  { id: 'aup', title: '13. Acceptable Use Policy' },
  { id: 'marketplace', title: '14. Compliance with Marketplace Policies' },
  { id: 'data-protection', title: '15. Data Protection & Amazon Information' },
  { id: 'confidentiality', title: '16. Confidentiality' },
  { id: 'ip', title: '17. Intellectual Property' },
  { id: 'trademarks', title: '18. Trademark Notice' },
  { id: 'term', title: '19. Term & Termination' },
  { id: 'force-majeure', title: '20. Force Majeure' },
  { id: 'indemnification', title: '21. Indemnification' },
  { id: 'warranty', title: '22. Disclaimer of Warranties' },
  { id: 'limitation', title: '23. Limitation of Liability' },
  { id: 'governing-law', title: '24. Governing Law & Disputes' },
  { id: 'general', title: '25. General' },
  { id: 'contact', title: '26. Contact' },
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="3PL Terms of Service"
        description="Master Services Agreement for Shipmaster Fulfillment. Standard 3PL terms covering services, pricing, liability, and operations. Last updated February 2026."
        noindex={true}
      />
      <Header />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">Legal</p>
          <h1 className="mt-2 text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Terms of Service
          </h1>
          <p className="mt-3 text-slate-500 text-sm">Last updated: {LAST_UPDATED}</p>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            These Terms of Service (“Terms”) govern the use of services provided by Shipmaster
            Fulfillment LLC, a Florida limited liability company (“Shipmaster,” “we,” “us,” or
            “our”), and form a legally binding agreement between Shipmaster and the
            individual or entity that uses our services or website (“Client,” “you,” or “your”).
            Please read them carefully.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
          <aside className="lg:sticky lg:top-28 self-start">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 max-h-[80vh] overflow-y-auto">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
                On this page
              </p>
              <ul className="space-y-1.5">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="flex items-start gap-1.5 text-sm text-slate-700 hover:text-indigo-600 font-medium transition-colors"
                    >
                      <ChevronRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
                      <span>{s.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <article>
            <Section id="agreement" title="1. Agreement to Terms">
              <p>
                By using shipmasterprep.com, requesting an estimate, executing a Statement of Work
                (“SOW”) or service order with us, or storing inventory in our warehouse, you agree
                to be bound by these Terms. If you do not agree, do not use our services. If you
                are entering into these Terms on behalf of a company, you represent that you have
                authority to bind that company.
              </p>
            </Section>

            <Section id="definitions" title="2. Definitions">
              <ul>
                <li><strong>Services</strong> — the warehousing, receiving, kitting, pick-and-pack, shipping, and related logistics services that Shipmaster provides.</li>
                <li><strong>Goods</strong> — the inventory you ship to our facility for storage and fulfillment.</li>
                <li><strong>End Customer</strong> — the recipient of an outbound shipment.</li>
                <li><strong>SOW</strong> — a written statement of work, service order, or pricing schedule that, together with these Terms, governs a specific engagement.</li>
              </ul>
            </Section>

            <Section id="services" title="3. Services Provided">
              <p>
                Shipmaster provides 3PL services from our facility in Weston, Florida, including:
                receiving inbound shipments, performing quality checks, storing Goods on a
                pallet/shelf basis, picking and packing orders received from connected
                storefronts, generating shipping labels with carriers we have contracts with
                (including UPS, FedEx, and USPS), tendering parcels to those carriers, processing
                returns, and providing reports and tracking visibility. Specific scope is
                documented in your SOW.
              </p>
            </Section>

            <Section id="account" title="4. Account & Onboarding">
              <p>
                You are responsible for the accuracy of information you provide during onboarding,
                including your storefront integrations, SKU master data, product weights and
                dimensions, packaging requirements, and ship-from settings. You must keep
                credentials secure and notify us immediately of any unauthorized access.
              </p>
            </Section>

            <Section id="pricing" title="5. Pricing & Payment">
              <ul>
                <li>Our published pricing (e.g., $2 per order pick-and-pack, $25 per pallet prorated storage) and any negotiated rates in your SOW form the basis of our fees.</li>
                <li>Carrier shipping costs are passed through using the rates we negotiate with carriers; we may take a small margin disclosed in your SOW.</li>
                <li>Invoices are issued on the cadence stated in your SOW (typically weekly or monthly) and are due net 7 days unless otherwise agreed.</li>
                <li>Late payments accrue interest of 1.5% per month or the maximum allowed by law, whichever is lower. We may suspend Services for accounts more than 15 days past due.</li>
                <li>You authorize Shipmaster to charge your designated payment method for fees, surcharges, and any pass-through carrier costs.</li>
              </ul>
            </Section>

            <Section id="inventory" title="6. Inventory, Receiving & Storage">
              <ul>
                <li>You must give advance notice of inbound shipments and provide a packing list. Unannounced inbounds may be refused or charged a special-handling fee.</li>
                <li>We perform a reasonable visual inspection on receipt and document discrepancies (counts, damage, mis-labeled SKUs). Concealed damage or shortages discovered after receipt should be reported within 5 business days.</li>
                <li>Storage is billed by the pallet position (or case rate where applicable) and prorated based on actual utilization, as defined in your SOW.</li>
                <li>Title to Goods remains with Client. Shipmaster acts only as a bailee and does not claim ownership.</li>
                <li>Goods left in our facility for more than 90 days after termination, or after receipt of a written demand to remove them, may be deemed abandoned and disposed of at Client expense, with the proceeds (if any) applied to outstanding invoices.</li>
              </ul>
            </Section>

            <Section id="fulfillment" title="7. Order Fulfillment">
              <ul>
                <li>We aim to ship orders received before our daily cutoff (currently 1:00 PM ET on business days) the same business day, subject to inventory availability and carrier pickups.</li>
                <li>Holidays, weather events, and high-volume periods may extend this timeline; we will provide reasonable notice when feasible.</li>
                <li>You are responsible for ensuring product images, SKU data, and order data sent through your storefront are accurate. Shipmaster is not liable for shipments fulfilled per the order data we received.</li>
              </ul>
            </Section>

            <Section id="shipping" title="8. Shipping & Carriers">
              <ul>
                <li>Shipmaster tenders parcels to common carriers (UPS, FedEx, USPS, regional carriers) on Client’s behalf. Once tendered, the carrier is responsible for transit, delivery, and any in-transit damage or loss subject to the carrier’s tariff and any insurance coverage.</li>
                <li>Transit time estimates and zone maps published on shipmasterprep.com are based on standard ground service from origin ZIP 33326 and are estimates only.</li>
                <li>Shipmaster is not a carrier and does not guarantee delivery times.</li>
                <li>Address corrections, returned-to-sender fees, dimensional weight adjustments, and other carrier surcharges are passed through to Client.</li>
              </ul>
            </Section>

            <Section id="restricted" title="9. Restricted & Prohibited Items">
              <p>You agree not to ship to our facility, and not to instruct us to handle, any of the following without prior written approval:</p>
              <ul>
                <li>Hazardous materials (hazmat), flammables, corrosives, explosives, or compressed gases.</li>
                <li>Live animals, plants, or perishable items unless covered in your SOW.</li>
                <li>Firearms, ammunition, or weapons.</li>
                <li>Tobacco, vape, alcohol, cannabis, or CBD products outside permitted jurisdictions.</li>
                <li>Currency, precious metals, jewelry above the limits in your SOW, or items requiring special licensing.</li>
                <li>Counterfeit goods, items that infringe third-party rights, or items prohibited by law or carrier policy.</li>
              </ul>
              <p>
                Shipmaster may refuse, segregate, or return such items at Client’s expense and is
                not liable for any losses associated with them.
              </p>
            </Section>

            <Section id="returns" title="10. Returns Processing">
              <p>
                When enabled in your SOW, we receive and process returns: inspecting items,
                applying disposition codes (restock, quarantine, dispose), and updating your
                storefront. Specific fees and dispositions are described in the SOW. Returns that
                arrive damaged or non-compliant may be charged at our standard handling rate.
              </p>
            </Section>

            <Section id="liability" title="11. Risk of Loss, Insurance & Liability for Goods">
              <ul>
                <li>Risk of loss for inbound shipments transfers to Shipmaster upon physical receipt at our dock and remains with us until parcels are tendered to the outbound carrier.</li>
                <li>Our maximum aggregate liability for loss of or damage to Goods while in our care is the lesser of (a) the documented wholesale cost of the affected Goods or (b) $0.50 per pound of the affected Goods, unless higher coverage is purchased through us in writing.</li>
                <li>Client is encouraged to maintain its own commercial property and inventory insurance covering Goods stored at our facility.</li>
                <li>Once parcels are tendered to a carrier, the carrier’s tariff governs in-transit liability and any insurance coverage you have purchased applies.</li>
              </ul>
            </Section>

            <Section id="client-obligations" title="12. Client Obligations">
              <ul>
                <li>Maintain accurate inventory data, product information, and connected storefront credentials.</li>
                <li>Pay invoices on time.</li>
                <li>Comply with all applicable laws, including those governing product labeling, import/export, sales tax, and consumer protection.</li>
                <li>Not use our services to violate the rights of any third party or to send unsolicited mailings.</li>
              </ul>
            </Section>

            <Section id="aup" title="13. Acceptable Use Policy">
              <p>
                You agree not to use our website, our services, or any data or systems we provide
                access to (&ldquo;Shipmaster Resources&rdquo;) to do any of the following, and not
                to assist or permit any third party to do any of the following:
              </p>
              <ul>
                <li>Violate any applicable law, regulation, or third-party right (including intellectual-property, privacy, and consumer-protection rights).</li>
                <li>Send unsolicited commercial messages, spam, or chain mail; engage in deceptive marketing or false advertising.</li>
                <li>Defraud, mislead, or deceive Shipmaster, any end customer, or any marketplace (including Amazon).</li>
                <li>Probe, scan, or test the vulnerability of any system or network without our prior written consent; bypass or circumvent authentication, rate limits, or other technical controls.</li>
                <li>Introduce malware, ransomware, viruses, trojans, worms, or any other malicious code; interfere with the integrity, performance, or availability of Shipmaster Resources.</li>
                <li>Reverse engineer, decompile, or attempt to extract the source code or trade secrets of Shipmaster Resources, except to the limited extent permitted by applicable law.</li>
                <li>Use Shipmaster Resources to build a competing product or service, or to scrape, harvest, or aggregate data we provide for any purpose other than the Services we agreed to provide to you.</li>
                <li>Submit, transmit, or store information that is unlawful, defamatory, harassing, obscene, or that infringes any third-party right.</li>
                <li>Resell, sublicense, or otherwise commercialize Shipmaster Resources without our prior written consent.</li>
              </ul>
              <p>
                Shipmaster may suspend or terminate access to any Shipmaster Resource if we
                reasonably believe you are violating this Acceptable Use Policy. Repeated or
                material violations may result in termination of these Terms for cause.
              </p>
            </Section>

            <Section id="marketplace" title="14. Compliance with Marketplace Policies">
              <p>
                You acknowledge that fulfilling orders for marketplaces and storefronts (including
                Amazon, Shopify, Etsy, eBay, TikTok Shop, Walmart, and others) is subject to those
                providers&rsquo; own policies and that you remain responsible for your account&rsquo;s
                compliance with them. Without limiting the foregoing:
              </p>
              <ul>
                <li>You will only authorize Shipmaster to access marketplace data (including via Amazon&rsquo;s Selling Partner API) for accounts you own or are authorized to manage.</li>
                <li>You will not direct Shipmaster to perform any action that would violate Amazon&rsquo;s{' '}
                  <a
                    href="https://sellercentral.amazon.com/mws/static/policy?documentType=AUP&locale=en_US"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 font-semibold hover:underline"
                  >
                    Acceptable Use Policy
                  </a>{' '}
                  or{' '}
                  <a
                    href="https://sellercentral.amazon.com/mws/static/policy?documentType=DPP&locale=en_US"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 font-semibold hover:underline"
                  >
                    Data Protection Policy
                  </a>
                  , or any equivalent policy of another marketplace.
                </li>
                <li>You will provide accurate listing data, product safety information, and labeling required by each marketplace and by applicable law.</li>
                <li>You will not request fulfillment of orders that involve restricted, recalled, counterfeit, or otherwise prohibited goods (see Section 9).</li>
              </ul>
              <p>
                Shipmaster will perform Services in a manner consistent with these obligations and
                may decline any instruction that we reasonably believe would violate marketplace
                policy or applicable law.
              </p>
            </Section>

            <Section id="data-protection" title="15. Data Protection & Amazon Information">
              <p>
                Each party will comply with applicable data-protection laws in connection with
                personal information processed under these Terms. Shipmaster&rsquo;s collection,
                use, storage, and deletion of personal information is described in our{' '}
                <Link to="/privacy" className="text-indigo-600 font-semibold hover:underline">
                  Privacy Policy
                </Link>
                , which is incorporated into these Terms by reference.
              </p>
              <p>
                Where Shipmaster accesses or receives information originating from Amazon services
                (including via the Selling Partner API) on Client&rsquo;s behalf
                (&ldquo;Amazon Information&rdquo;), Shipmaster will:
              </p>
              <ul>
                <li>Use Amazon Information solely to provide the Services to Client;</li>
                <li>Comply with Amazon&rsquo;s Data Protection Policy, Acceptable Use Policy, and Solution Provider Agreement, as updated from time to time;</li>
                <li>Apply the technical and organizational safeguards described in our Privacy Policy (including encryption in transit and at rest, least-privilege access, and logging);</li>
                <li>Not use Amazon Information for advertising, remarketing, or any purpose competitive with Amazon, Client, or any other selling partner;</li>
                <li>Not transfer Amazon Information to any third party other than approved sub-processors and shipping carriers as needed to perform the Services;</li>
                <li>Delete or anonymize Amazon Information within 30 days following termination, on Client&rsquo;s request, or on revocation of SP-API authorization, except for the limited records we are required to retain by law or by Amazon&rsquo;s policies; and</li>
                <li>Promptly notify Client of any confirmed security incident affecting Amazon Information, in any event no later than 24 hours after we become aware of it.</li>
              </ul>
              <p>
                Client represents that it has the legal right to authorize Shipmaster&rsquo;s
                access to any Amazon Information it provides or makes accessible to us.
              </p>
            </Section>

            <Section id="confidentiality" title="16. Confidentiality">
              <p>
                Each party agrees to keep the other party&rsquo;s non-public business, technical, and
                customer information confidential and to use it only as necessary to perform under
                these Terms. Confidentiality obligations survive termination for three years.
                End-customer information shared with Shipmaster will be handled in accordance with
                our Privacy Policy.
              </p>
            </Section>

            <Section id="ip" title="17. Intellectual Property">
              <p>
                You retain all rights to your trademarks, product images, packaging designs, and
                other materials provided to us, and you grant Shipmaster a limited license to use
                them solely to perform the Services. Shipmaster retains all rights to its
                website, software, processes, and proprietary methods.
              </p>
            </Section>

            <Section id="trademarks" title="18. Trademark Notice">
              <p>
                &ldquo;Shipmaster&rdquo; and the Shipmaster logo are trademarks of Shipmaster
                Fulfillment LLC. All other product names, logos, brands, and trademarks referenced
                on shipmasterprep.com — including but not limited to Amazon, Shopify, Etsy, eBay,
                TikTok Shop, Walmart, BigCommerce, Squarespace, Wix, UPS, FedEx, and USPS — are the
                property of their respective owners. Reference to any third-party trademark is for
                identification only and does not imply endorsement, sponsorship, or affiliation.
              </p>
              <p>
                Shipmaster Fulfillment LLC is an independent third-party logistics provider. We
                are not affiliated with, endorsed by, or sponsored by Amazon.com, Inc. or any of
                its affiliates.
              </p>
            </Section>

            <Section id="term" title="19. Term & Termination">
              <ul>
                <li>These Terms remain in effect while you use our Services.</li>
                <li>Either party may terminate for convenience on 30 days’ written notice.</li>
                <li>Either party may terminate for cause if the other materially breaches these Terms and fails to cure within 15 days of written notice.</li>
                <li>Upon termination, you must arrange for the removal of your Goods from our facility within 30 days. Storage and handling fees continue to accrue until the Goods are removed.</li>
                <li>Sections that by their nature should survive termination (including 5, 11, 13, 15, 16, 17, 18, 21, 23, and 24) will survive.</li>
              </ul>
            </Section>

            <Section id="force-majeure" title="20. Force Majeure">
              <p>
                Neither party will be liable for failure or delay in performance caused by events
                beyond its reasonable control, including hurricanes and other severe weather,
                fires, floods, pandemics, labor disputes, carrier disruptions, governmental
                actions, internet or utility outages, and acts of war or terrorism.
              </p>
            </Section>

            <Section id="indemnification" title="21. Indemnification">
              <p>
                You agree to indemnify and hold harmless Shipmaster, its officers, employees, and
                agents from any third-party claims, damages, losses, and reasonable attorneys’
                fees arising out of: (a) your Goods, including product defects, recalls, or
                regulatory violations; (b) your breach of these Terms; (c) your storefront data,
                content, or marketing claims; or (d) your violation of applicable law. Shipmaster
                will indemnify you for third-party claims arising from our gross negligence or
                willful misconduct in performing the Services.
              </p>
            </Section>

            <Section id="warranty" title="22. Disclaimer of Warranties">
              <p>
                Except as expressly set out in these Terms or your SOW, the Services and our
                website are provided “as is” and “as available.” We disclaim all other warranties,
                express or implied, including merchantability, fitness for a particular purpose,
                title, and non-infringement. We do not warrant uninterrupted or error-free service.
              </p>
            </Section>

            <Section id="limitation" title="23. Limitation of Liability">
              <p>
                To the maximum extent permitted by law, neither party will be liable for indirect,
                incidental, consequential, special, exemplary, or punitive damages, or for lost
                profits, lost revenue, or loss of business, even if advised of the possibility of
                such damages.
              </p>
              <p>
                Except for (a) Shipmaster’s liability for Goods set forth in Section 11, (b) a
                party’s indemnification obligations, or (c) a party’s gross negligence or willful
                misconduct, each party’s aggregate liability under these Terms will not exceed the
                fees paid or payable by Client to Shipmaster in the six months preceding the event
                giving rise to the claim.
              </p>
            </Section>

            <Section id="governing-law" title="24. Governing Law & Disputes">
              <p>
                These Terms are governed by the laws of the State of Florida, without regard to
                conflict-of-law principles. The parties agree to attempt in good faith to resolve
                any dispute through informal discussion before pursuing formal proceedings. Any
                action arising from these Terms will be brought exclusively in the state or
                federal courts located in Broward County, Florida, and each party consents to
                personal jurisdiction there.
              </p>
            </Section>

            <Section id="general" title="25. General">
              <ul>
                <li><strong>Entire Agreement.</strong> These Terms, together with your SOW and our Privacy Policy, are the entire agreement between the parties on this subject.</li>
                <li><strong>Order of Precedence.</strong> If there is a conflict, the SOW controls over these Terms.</li>
                <li><strong>Amendments.</strong> We may update these Terms from time to time. Material changes will be communicated by email or a notice on our site, and continued use of the Services after that constitutes acceptance.</li>
                <li><strong>Assignment.</strong> Neither party may assign these Terms without the other’s prior written consent, except that either party may assign to an affiliate or in connection with a merger, acquisition, or sale of substantially all assets.</li>
                <li><strong>Severability.</strong> If any provision is held unenforceable, the remaining provisions remain in effect.</li>
                <li><strong>No Waiver.</strong> A failure to enforce any provision is not a waiver of the right to enforce it later.</li>
                <li><strong>Notices.</strong> Notices must be sent in writing to the email address on file (for Shipmaster, info@shipmasterprep.com).</li>
                <li><strong>Independent Contractors.</strong> The parties are independent contractors. Nothing in these Terms creates an agency, partnership, or joint venture.</li>
              </ul>
            </Section>

            <Section id="contact" title="26. Contact">
              <p>
                Shipmaster Fulfillment LLC
                <br />
                Weston, FL
                <br />
                <a href="mailto:info@shipmasterprep.com" className="text-indigo-600 font-semibold hover:underline">
                  info@shipmasterprep.com
                </a>
              </p>
            </Section>

            <div className="mt-12 pt-8 border-t border-slate-200 flex items-center gap-4 text-sm">
              <Link to="/privacy" className="text-indigo-600 font-semibold hover:underline">
                View Privacy Policy →
              </Link>
              <span className="text-slate-300">·</span>
              <Link to="/" className="text-slate-600 font-medium hover:text-indigo-600">
                Back to home
              </Link>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-28">
      <h2 className="text-2xl font-extrabold text-slate-900 mb-4">{title}</h2>
      <div className="text-slate-700 leading-relaxed space-y-4">{children}</div>
    </section>
  );
}
